import { type ComputedRef, Ref, computed, ref, useStore, watch } from "@nuxtjs/composition-api";

import { useDebounce } from "@/composables/utils";
import { useCreatorShopPostsStore, useCreatorShopSearchStore } from "@/store";
import { useCreatorProductStore } from "@/store/creator-products";
import { getExperimentsStore } from "@/store/experiments";
import { SEARCH_DEFAULT_PAGE_SIZE, SEARCH_INPUT_DEBOUNCE_DELAY } from "@/utils/constants";
import { FEATURE_FLAG_ACTIVE, FLAG_CREATOR_SHOP_SEARCH } from "@/utils/experiments/constants";

type useCreatorShopProductsReturn = {
  searchTerm: Ref<string>;
  showLoading: ComputedRef<boolean>;
  showError: ComputedRef<boolean>;
  productIds: ComputedRef<Array<string>>;
  onFetchMoreProducts: () => void;
  onFetchProducts: (term?: string) => void;
};

type useCreatorShopPostReturn = {
  searchTerm: Ref<string | null>;
  showLoading: ComputedRef<boolean>;
  showError: ComputedRef<boolean>;
  ltkIds: ComputedRef<Array<string>>;
  onFetchMorePosts: () => void;
  onFetchPosts: (term?: string) => void;
};

export const useCreatorShopRecentProducts = (profileId: string): useCreatorShopProductsReturn => {
  if (!profileId) {
    throw new Error("useCreatorShopRecentProducts() requires a profileId be supplied.");
  }

  const store = useStore();
  const creatorShopSearchStore = useCreatorShopSearchStore();
  const experimentsStore = getExperimentsStore(store);
  const creatorProductStore = useCreatorProductStore();
  const isTyping = ref(false);

  const searchPage = ref(1);
  const searchLoading = ref(false);
  const searchPaginating = ref(false);
  const searchError = ref(false);

  const searchTerm = ref("");

  const isCreatorShopSearchEnabled = computed(
    () => experimentsStore.flag(FLAG_CREATOR_SHOP_SEARCH)?.value === FEATURE_FLAG_ACTIVE,
  );

  const showLoading = computed(() => {
    if (isCreatorShopSearchEnabled.value) {
      return Boolean(isTyping.value || searchLoading.value);
    }
    return Boolean(
      isTyping.value ||
        creatorProductStore.recentProductsFetchState.pending ||
        creatorProductStore.recentProductsSearchFetchState.pending,
    );
  });

  const showError = computed(() => {
    if (isCreatorShopSearchEnabled.value) {
      return searchError.value;
    }
    return Boolean(
      creatorProductStore.recentProductsFetchState.error || creatorProductStore.recentProductsSearchFetchState.error,
    );
  });

  const legacyFeedIds = computed(() => {
    const searchTermValue = searchTerm.value;
    if (searchTermValue) {
      return creatorProductStore.recentProductsSearchFeed[searchTermValue]?.ids ?? [];
    }
    return creatorProductStore.recentProductsFeed[profileId]?.ids ?? [];
  });

  const feedIds = computed(() => {
    const searchTermValue = searchTerm.value;
    if (searchTermValue) {
      return creatorShopSearchStore.productsSearchFeedMap[searchTermValue]?.items.map(({ id }) => id) ?? [];
    }
    return creatorShopSearchStore.productsFeedMap[profileId]?.items.map(({ id }) => id) ?? [];
  });

  const productIds = computed(() => {
    const isCreatorShopSearchEnabledValue = isCreatorShopSearchEnabled.value;
    const legacyFeedIdsValue = legacyFeedIds.value;
    const feedIdsValue = feedIds.value;

    if (isCreatorShopSearchEnabledValue) {
      return feedIdsValue;
    } else {
      return legacyFeedIdsValue;
    }
  });

  const hasMore = computed(() => {
    const searchTermValue = searchTerm.value;
    let productsFeed = creatorShopSearchStore.productsFeedMap[profileId];
    if (searchTermValue) {
      productsFeed = creatorShopSearchStore.productsSearchFeedMap[searchTermValue];
    }

    if (!productsFeed) {
      return false;
    }
    const productsTotal = productsFeed.total;
    const productsTotalPages = Math.ceil(productsTotal / SEARCH_DEFAULT_PAGE_SIZE);
    const productsComplete = productsFeed.complete;
    const productsCurrentPage = searchPage.value;
    if (!productsComplete) {
      return true;
    }
    return productsCurrentPage < productsTotalPages;
  });

  const onFetchMoreProducts = async () => {
    if (isCreatorShopSearchEnabled.value) {
      if (!hasMore.value || searchLoading.value || searchPaginating.value || isTyping.value) {
        return; // do not paginate if already loading or no more products
      }
      try {
        searchPage.value += 1; // increment the page for the next fetch
        searchPaginating.value = true; // set paginating state to true
        await creatorShopSearchStore.fetchMoreProductSearchResults(profileId, {
          query: searchTerm.value,
          page: searchPage.value,
          limit: SEARCH_DEFAULT_PAGE_SIZE,
        });
      } catch {
        searchError.value = true; // set error state if the fetch fails
      } finally {
        searchPaginating.value = false; // reset paginating state
      }
    } else if (searchTerm.value) {
      await creatorProductStore.searchCreatorRecentProducts(profileId, searchTerm.value);
    } else {
      await creatorProductStore.fetchCreatorRecentProducts(profileId);
    }
  };

  const onFetchProducts = async (term = "") => {
    isTyping.value = false; // let fetch state take over the loading indicator
    if (isCreatorShopSearchEnabled.value) {
      try {
        searchError.value = false;
        searchPage.value = 1; // reset the page to 1 for the new search term
        searchLoading.value = true; // set loading to true while we fetch
        await creatorShopSearchStore.fetchProductSearchResults(profileId, {
          query: term,
          page: searchPage.value,
          limit: SEARCH_DEFAULT_PAGE_SIZE,
        });
      } catch {
        searchError.value = true; // set error state if the fetch fails
      } finally {
        searchLoading.value = false; // reset loading state
      }
    } else {
      await creatorProductStore.searchCreatorRecentProducts(profileId, term);
    }
  };

  const debouncedOnFetchProducts = useDebounce(onFetchProducts, SEARCH_INPUT_DEBOUNCE_DELAY);

  watch(
    () => searchTerm.value,
    (value: string) => {
      debouncedOnFetchProducts.cancel(); // cancel any previous call as the user is still interacting
      creatorProductStore.clearRecentProductsSearchFeed(); // clear previous searches, as we don't index them by profile just term
      creatorShopSearchStore.resetSearch();

      if (value) {
        isTyping.value = true; // show the loading indicator even though a fetch is technically not occurring
        debouncedOnFetchProducts(value);
      } else {
        isTyping.value = false; // the user cleared or deleted the contest of search
      }
    },
  );

  return {
    searchTerm,
    showLoading,
    showError,
    productIds,
    onFetchProducts,
    onFetchMoreProducts,
  };
};

export const useCreatorShopRecentPosts = (profileId: string): useCreatorShopPostReturn => {
  if (!profileId) {
    throw new Error("useCreatorShopRecentPosts() requires a profileId be supplied.");
  }

  const store = useStore();
  const creatorShopSearchStore = useCreatorShopSearchStore();
  const experimentsStore = getExperimentsStore(store);
  const creatorShopPostsStore = useCreatorShopPostsStore();
  const isTyping = ref(false);

  const searchPage = ref(1);
  const searchLoading = ref(false);
  const searchPaginating = ref(false);
  const searchError = ref(false);

  const searchTerm = ref("");

  const isCreatorShopSearchEnabled = computed(
    () => experimentsStore.flag(FLAG_CREATOR_SHOP_SEARCH)?.value === FEATURE_FLAG_ACTIVE,
  );

  const searchTermModel = computed({
    get: () => {
      const isCreatorShopSearchEnabledValue = isCreatorShopSearchEnabled.value;
      const searchTermValue = searchTerm.value;
      const termValue = creatorShopPostsStore.term;
      if (isCreatorShopSearchEnabledValue) {
        return searchTermValue;
      }
      return termValue;
    },
    set: (newSearchTerm: string | null) => {
      const isCreatorShopSearchEnabledValue = isCreatorShopSearchEnabled.value;
      if (isCreatorShopSearchEnabledValue) {
        searchTerm.value = newSearchTerm || "";
      } else {
        creatorShopPostsStore.term = newSearchTerm;
      }
    },
  });

  const showLoading = computed(() => {
    if (isCreatorShopSearchEnabled.value) {
      return Boolean(isTyping.value || searchLoading.value);
    }
    return Boolean(isTyping.value || creatorShopPostsStore.fetchState.pending);
  });

  const showError = computed(() => {
    if (isCreatorShopSearchEnabled.value) {
      return searchError.value;
    }
    return Boolean(creatorShopPostsStore.fetchState.error);
  });

  const legacyFeedIds = computed(() => {
    const searchTermValue = searchTerm.value;
    if (searchTermValue) {
      return creatorShopPostsStore._searchFeeds[searchTermValue]?.ids ?? [];
    }
    return creatorShopPostsStore._shopFeeds[profileId]?.ids ?? [];
  });

  const feedIds = computed(() => {
    const searchTermValue = searchTerm.value;
    if (searchTermValue) {
      return creatorShopSearchStore.ltksSearchFeedMap[searchTermValue]?.items.map(({ id }) => id) ?? [];
    }
    return creatorShopSearchStore.ltksFeedMap[profileId]?.items.map(({ id }) => id) ?? [];
  });

  const ltkIds = computed(() => {
    const isCreatorShopSearchEnabledValue = isCreatorShopSearchEnabled.value;
    const legacyFeedIdsValue = legacyFeedIds.value;
    const feedIdsValue = feedIds.value;

    if (isCreatorShopSearchEnabledValue) {
      return feedIdsValue;
    } else {
      return legacyFeedIdsValue;
    }
  });

  const hasMore = computed(() => {
    const searchTermValue = searchTerm.value;
    let postsFeed = creatorShopSearchStore.ltksFeedMap[profileId];
    if (searchTermValue) {
      postsFeed = creatorShopSearchStore.ltksSearchFeedMap[searchTermValue];
    }

    if (!postsFeed) {
      return false;
    }

    const ltksTotal = postsFeed.total;
    const ltksTotalPages = Math.ceil(ltksTotal / SEARCH_DEFAULT_PAGE_SIZE);
    const ltksComplete = postsFeed.complete;
    const ltksCurrentPage = searchPage.value;
    if (!ltksComplete) {
      return true;
    }
    return ltksCurrentPage < ltksTotalPages;
  });

  const onFetchMorePosts = async () => {
    if (isCreatorShopSearchEnabled.value) {
      if (!hasMore.value || searchLoading.value || searchPaginating.value || isTyping.value) {
        return; // do not paginate if already loading or no more products
      }
      try {
        searchPage.value += 1; // increment the page for the next fetch
        searchPaginating.value = true; // set paginating state to true
        await creatorShopSearchStore.fetchMoreLtkSearchResults(profileId, {
          query: searchTermModel.value || "",
          page: searchPage.value,
          limit: SEARCH_DEFAULT_PAGE_SIZE,
        });
      } catch {
        searchError.value = true; // set error state if the fetch fails
      } finally {
        searchPaginating.value = false; // reset paginating state
      }
    } else if (searchTermModel.value) {
      await creatorShopPostsStore.fetchBySearchTerm(profileId);
    } else {
      await creatorShopPostsStore.fetch(profileId);
    }
  };

  const onFetchPosts = async (term = "") => {
    isTyping.value = false; // let fetch state take over the loading indicator
    if (isCreatorShopSearchEnabled.value) {
      try {
        searchError.value = false;
        searchPage.value = 1; // reset the page to 1 for the new search term
        searchLoading.value = true; // set loading to true while we fetch
        await creatorShopSearchStore.fetchLtkSearchResults(profileId, {
          query: term,
          page: searchPage.value,
          limit: SEARCH_DEFAULT_PAGE_SIZE,
        });
      } catch {
        searchError.value = true; // set error state if the fetch fails
      } finally {
        searchLoading.value = false; // reset loading state
      }
    } else {
      await creatorShopPostsStore.fetchBySearchTerm(profileId);
    }
  };

  const debouncedOnFetchPosts = useDebounce(onFetchPosts, SEARCH_INPUT_DEBOUNCE_DELAY);

  watch(
    () => searchTermModel.value,
    (value: string | null) => {
      debouncedOnFetchPosts.cancel(); // cancel any previous call as the user is still interacting
      creatorShopPostsStore.$resetSearch(); // clear previous searches, as we don't index them by profile just term
      creatorShopSearchStore.resetSearch();

      if (value) {
        isTyping.value = true; // show the loading indicator even though a fetch is technically not occurring
        debouncedOnFetchPosts(value);
      } else {
        isTyping.value = false; // the user cleared or deleted the contest of search
      }
    },
  );

  return {
    searchTerm: searchTermModel,
    showLoading,
    showError,
    ltkIds,
    onFetchPosts,
    onFetchMorePosts,
  };
};
